/**
 * usePendingChanges Hook
 *
 * Manages pending changes (INSERT, UPDATE, DELETE) for table explorer edit mode.
 * Provides helpers to add, modify, and remove changes, as well as utilities
 * to check if a row/cell has pending changes.
 */

import { useCallback, useMemo } from 'react';
import type {
  PendingChange,
  PendingInsert,
  PendingUpdate,
  PendingDelete,
  TableExplorerColumn,
} from '../../core/types/workspaceTypes';

type UsePendingChangesParams = {
  pendingChanges: PendingChange[];
  onChangesUpdate: (changes: PendingChange[]) => void;
  primaryKeyColumns: string[];
  columns: TableExplorerColumn[];
};

type UsePendingChangesResult = {
  // State
  pendingChanges: PendingChange[];
  hasChanges: boolean;
  changeCount: number;
  insertCount: number;
  updateCount: number;
  deleteCount: number;

  // Actions
  addInsert: () => string; // Returns temp ID
  updateInsert: (tempId: string, field: string, value: unknown) => void;
  removeInsert: (tempId: string) => void;

  addUpdate: (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => void;
  removeUpdate: (primaryKey: Record<string, unknown>, field: string) => void;

  addDelete: (primaryKey: Record<string, unknown>, originalRow: Record<string, unknown>) => void;
  removeDelete: (primaryKey: Record<string, unknown>) => void;

  discardAllChanges: () => void;

  // Utilities
  isRowDeleted: (primaryKey: Record<string, unknown>) => boolean;
  isRowInserted: (tempId: string) => boolean;
  getCellChange: (primaryKey: Record<string, unknown>, field: string) => PendingUpdate | undefined;
  getInsertRow: (tempId: string) => PendingInsert | undefined;
  getPrimaryKeyFromRow: (row: Record<string, unknown>) => Record<string, unknown>;
};

/**
 * Generate a unique temporary ID for new rows
 */
function generateTempId(): string {
  return `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Compare two primary key objects for equality
 */
function primaryKeysMatch(pk1: Record<string, unknown>, pk2: Record<string, unknown>): boolean {
  const keys1 = Object.keys(pk1).sort();
  const keys2 = Object.keys(pk2).sort();

  if (keys1.length !== keys2.length) return false;
  if (keys1.some((k, i) => k !== keys2[i])) return false;

  return keys1.every((key) => pk1[key] === pk2[key]);
}

/**
 * Hook to manage pending changes in table explorer edit mode
 */
export function usePendingChanges({
  pendingChanges,
  onChangesUpdate,
  primaryKeyColumns,
  columns,
}: UsePendingChangesParams): UsePendingChangesResult {
  // Memoized counts
  const insertCount = useMemo(
    () => pendingChanges.filter((c) => c.type === 'insert').length,
    [pendingChanges]
  );
  const updateCount = useMemo(
    () => pendingChanges.filter((c) => c.type === 'update').length,
    [pendingChanges]
  );
  const deleteCount = useMemo(
    () => pendingChanges.filter((c) => c.type === 'delete').length,
    [pendingChanges]
  );
  const changeCount = pendingChanges.length;
  const hasChanges = changeCount > 0;

  // Get primary key from a row
  const getPrimaryKeyFromRow = useCallback(
    (row: Record<string, unknown>): Record<string, unknown> => {
      const pk: Record<string, unknown> = {};
      for (const col of primaryKeyColumns) {
        pk[col] = row[col];
      }
      return pk;
    },
    [primaryKeyColumns]
  );

  // Add a new insert (returns temp ID)
  const addInsert = useCallback((): string => {
    const tempId = generateTempId();
    // Create empty row - columns start as undefined (not set)
    // undefined = omit from INSERT (use DB default)
    // null = explicitly set to NULL
    // Skip identity columns (they are auto-generated by the database)
    const row: Record<string, unknown> = {};
    for (const col of columns) {
      // Skip identity columns - they shouldn't be included in INSERT
      if (col.isIdentity) continue;
      // Initialize as undefined - will be omitted from INSERT unless user sets a value
      row[col.name] = undefined;
    }

    const newInsert: PendingInsert = {
      type: 'insert',
      tempId,
      row,
    };

    onChangesUpdate([...pendingChanges, newInsert]);
    return tempId;
  }, [pendingChanges, onChangesUpdate, columns]);

  // Update a field in an inserted row
  const updateInsert = useCallback(
    (tempId: string, field: string, value: unknown) => {
      onChangesUpdate(
        pendingChanges.map((change) => {
          if (change.type === 'insert' && change.tempId === tempId) {
            return {
              ...change,
              row: { ...change.row, [field]: value },
            };
          }
          return change;
        })
      );
    },
    [pendingChanges, onChangesUpdate]
  );

  // Remove an insert
  const removeInsert = useCallback(
    (tempId: string) => {
      onChangesUpdate(
        pendingChanges.filter(
          (change) => !(change.type === 'insert' && change.tempId === tempId)
        )
      );
    },
    [pendingChanges, onChangesUpdate]
  );

  // Add or update a cell change
  const addUpdate = useCallback(
    (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => {
      // Check if there's already an update for this cell
      const existingIndex = pendingChanges.findIndex(
        (c) =>
          c.type === 'update' &&
          c.field === field &&
          primaryKeysMatch(c.primaryKey, primaryKey)
      );

      if (existingIndex >= 0) {
        // Update existing change
        const existing = pendingChanges[existingIndex] as PendingUpdate;
        // If new value equals original, remove the change
        if (existing.oldValue === newValue) {
          onChangesUpdate(pendingChanges.filter((_, i) => i !== existingIndex));
        } else {
          // Update to new value
          onChangesUpdate(
            pendingChanges.map((c, i) =>
              i === existingIndex ? { ...c, newValue } : c
            )
          );
        }
      } else {
        // Don't add if values are the same
        if (oldValue === newValue) return;

        // Add new change
        const newUpdate: PendingUpdate = {
          type: 'update',
          primaryKey,
          field,
          oldValue,
          newValue,
        };
        onChangesUpdate([...pendingChanges, newUpdate]);
      }
    },
    [pendingChanges, onChangesUpdate]
  );

  // Remove an update
  const removeUpdate = useCallback(
    (primaryKey: Record<string, unknown>, field: string) => {
      onChangesUpdate(
        pendingChanges.filter(
          (c) =>
            !(
              c.type === 'update' &&
              c.field === field &&
              primaryKeysMatch(c.primaryKey, primaryKey)
            )
        )
      );
    },
    [pendingChanges, onChangesUpdate]
  );

  // Mark a row for deletion
  const addDelete = useCallback(
    (primaryKey: Record<string, unknown>, originalRow: Record<string, unknown>) => {
      // Check if already deleted
      const isDeleted = pendingChanges.some(
        (c) => c.type === 'delete' && primaryKeysMatch(c.primaryKey, primaryKey)
      );
      if (isDeleted) return;

      // Also remove any pending updates for this row (they no longer matter)
      const filtered = pendingChanges.filter(
        (c) =>
          !(c.type === 'update' && primaryKeysMatch(c.primaryKey, primaryKey))
      );

      const newDelete: PendingDelete = {
        type: 'delete',
        primaryKey,
        originalRow,
      };

      onChangesUpdate([...filtered, newDelete]);
    },
    [pendingChanges, onChangesUpdate]
  );

  // Unmark a row from deletion
  const removeDelete = useCallback(
    (primaryKey: Record<string, unknown>) => {
      onChangesUpdate(
        pendingChanges.filter(
          (c) =>
            !(c.type === 'delete' && primaryKeysMatch(c.primaryKey, primaryKey))
        )
      );
    },
    [pendingChanges, onChangesUpdate]
  );

  // Discard all changes
  const discardAllChanges = useCallback(() => {
    onChangesUpdate([]);
  }, [onChangesUpdate]);

  // Check if a row is marked for deletion
  const isRowDeleted = useCallback(
    (primaryKey: Record<string, unknown>): boolean => {
      return pendingChanges.some(
        (c) => c.type === 'delete' && primaryKeysMatch(c.primaryKey, primaryKey)
      );
    },
    [pendingChanges]
  );

  // Check if a row is an inserted row
  const isRowInserted = useCallback(
    (tempId: string): boolean => {
      return pendingChanges.some(
        (c) => c.type === 'insert' && c.tempId === tempId
      );
    },
    [pendingChanges]
  );

  // Get the pending update for a specific cell
  const getCellChange = useCallback(
    (primaryKey: Record<string, unknown>, field: string): PendingUpdate | undefined => {
      return pendingChanges.find(
        (c) =>
          c.type === 'update' &&
          c.field === field &&
          primaryKeysMatch(c.primaryKey, primaryKey)
      ) as PendingUpdate | undefined;
    },
    [pendingChanges]
  );

  // Get insert row by temp ID
  const getInsertRow = useCallback(
    (tempId: string): PendingInsert | undefined => {
      return pendingChanges.find(
        (c) => c.type === 'insert' && c.tempId === tempId
      ) as PendingInsert | undefined;
    },
    [pendingChanges]
  );

  return {
    pendingChanges,
    hasChanges,
    changeCount,
    insertCount,
    updateCount,
    deleteCount,

    addInsert,
    updateInsert,
    removeInsert,

    addUpdate,
    removeUpdate,

    addDelete,
    removeDelete,

    discardAllChanges,

    isRowDeleted,
    isRowInserted,
    getCellChange,
    getInsertRow,
    getPrimaryKeyFromRow,
  };
}
